// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { findAppIntegrationByName } from '@api/portalServices';
import type { AppIntegrationFindByNameResponse } from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useQuery } from '@tanstack/react-query';

/**
 * Fetches an application integration by its app name (e.g. `epic-hyperdrive`).
 * Reads `tenantId` from the session context and threads it into the BFF call
 * so the request carries tenant scope on hosts where the URL does not (matches
 * the `useIdpUsers` / `findDirectories` precedent). The page guards the call
 * behind the SSO entitlement; the hook additionally defers until `tenantId` is
 * available so an empty tenant never reaches the BFF.
 */
export const useAppIntegrationQuery = (appName: string, opts?: { enabled?: boolean }) => {
  const { tenantId } = useSession();
  return useQuery<AppIntegrationFindByNameResponse, ApiError>({
    queryKey: [QueryKey.APP_INTEGRATION, appName, tenantId],
    queryFn: () => findAppIntegrationByName(appName, tenantId || ''),
    enabled: (opts?.enabled ?? true) && Boolean(tenantId),
    retry: false,
    staleTime: 0,
  });
};
